import { clsx } from 'clsx';
import { ScheduleTypeEnum } from '../../../../../../js/api';
import { STICKY_HEADER } from '../../../../utils/globalStyles';
import { AvailabilityUrlCopy } from '../AvailabilityUrlCopy';
import { ScheduleTypeSelect } from './ScheduleTypeSelect';

interface CalendarHeaderProps {
  availabilityUrl: string;
  /** True when the viewport is md+; gates desktop-only hints (e.g. shift-click). */
  isMdUp: boolean;
  scheduleType: ScheduleTypeEnum;
  onScheduleTypeChange: (next: ScheduleTypeEnum) => void;
  /** Disables the dropdown while a type change is in flight. */
  scheduleTypeChanging?: boolean;
}

// Swatch classes match the actual calendar day-cell bg classes 1:1 so the
// legend reads as a visual key for what's on the grid.
const LEGEND_ITEMS_ALLOW_MODE = [
  { label: 'Available', swatch: 'tw:bg-brand-green-0' },
  { label: 'Pending', swatch: 'tw:bg-amber-100 tw:border tw:border-amber-300' },
  { label: 'Confirmed', swatch: 'tw:bg-emerald-100 tw:border tw:border-emerald-300' },
  { label: 'Completed', swatch: 'tw:bg-zinc-200 tw:border tw:border-zinc-400' },
];

const LEGEND_ITEMS_BLOCK_MODE = [
  { label: 'Available', swatch: 'tw:bg-brand-green-0' },
  { label: 'Blocked', swatch: 'tw:bg-rose-100 tw:border tw:border-rose-300' },
  { label: 'Pending', swatch: 'tw:bg-amber-100 tw:border tw:border-amber-300' },
  { label: 'Confirmed', swatch: 'tw:bg-emerald-100 tw:border tw:border-emerald-300' },
  { label: 'Completed', swatch: 'tw:bg-zinc-200 tw:border tw:border-zinc-400' },
];

export const CalendarHeader = ({
  availabilityUrl,
  isMdUp,
  scheduleType,
  onScheduleTypeChange,
  scheduleTypeChanging,
}: CalendarHeaderProps) => {
  const isOpenMode = scheduleType === ScheduleTypeEnum.OPEN;
  const title = isOpenMode ? 'Block Dates' : 'Add Availability';
  const hint = isOpenMode
    ? 'Click on a date to block scheduling, click again to remove'
    : 'Click on a date to allow scheduling, click again to remove';
  const legendItems = isOpenMode ? LEGEND_ITEMS_BLOCK_MODE : LEGEND_ITEMS_ALLOW_MODE;

  return (
    <div className={clsx(STICKY_HEADER, 'tw:pb-3 tw:pt-2 tw:max-w-3xl')}>
      <div
        className={clsx(
          'tw:w-full tw:rounded-lg tw:border tw:p-3',
          'tw:bg-white tw:border-brand-green-1/50 tw:text-brand-green-1'
        )}
      >
        {/* Row 1: title (left) and share-availability link (right-aligned).
            Both short — fit comfortably on one row at all sizes. */}
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2 tw:mb-2">
          <div className="tw:text-base tw:font-semibold">{title}</div>
          <AvailabilityUrlCopy availabilityUrl={availabilityUrl} className="tw:mb-0" />
        </div>

        {/* Row 2: schedule-type dropdown. Sizes to the widest option label
            (no truncation) via the compact-styles `tw:w-fit` wrapper inside
            ScheduleTypeSelect. Stays left-aligned on all sizes. */}
        <div className="tw:mb-2">
          <ScheduleTypeSelect
            value={scheduleType}
            onChange={onScheduleTypeChange}
            disabled={scheduleTypeChanging}
          />
        </div>

        <div className="tw:!mt-0 tw:!pt-0 tw:!mb-1 tw:text-xs">
          {hint}
          {isMdUp && (
            <>
              {' · '}
              Shift+click a second date to apply the same action across a range
            </>
          )}
        </div>
        <ul className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:!m-0 tw:!p-0 tw:list-none">
          {legendItems.map(({ label, swatch }) => (
            <li key={label} className="tw:flex tw:items-center tw:gap-1.5">
              <span
                aria-hidden="true"
                className={clsx('tw:w-3 tw:h-3 tw:rounded-sm tw:inline-block', swatch)}
              />
              <span className="tw:text-xs tw:font-medium">{label}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
